"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import { getStrapiMedia } from "@/utils/api-helpers";
import { getBackgroundColor } from "@/utils/colorHelpers";

gsap.registerPlugin(ScrollTrigger);

type StepItem = {
  id: number;
  title: string;
  description: string;
  image?: {
    url: string;
    alternativeText?: string | null;
    name?: string;
  };

};

type PinnedStepsSectionProps = {
  data: {
    heading: string;
    steps: StepItem[];
    backgroundColor: {
      backgroundColor: string;
    };
  };
};

export default function PinnedStepsSection({ data }: PinnedStepsSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const background = getBackgroundColor(
    data.backgroundColor?.backgroundColor);
  const counterRefs: any = useRef(
    [...Array(data.steps?.length || 0)].map(() => React.createRef())
  );
  const [frame, setFrame] = useState(0);
  function updateCounter(index: number, action: string) {
    counterRefs.current[index].current.childNodes.forEach(
      (child: any, i: number) => {
        if (index === i) {
          if (action === "add") {
            child.classList.add("active");
          }
          if (action === "remove") {
            child.classList.remove("active");
          }
        }
      }
    );
  }
  useEffect(() => {
    const panels = document.querySelectorAll(".step-panel");

    panels.forEach((panel, index) => {
      const pinSpacing = index === panels.length - 1;

      gsap.timeline({
        scrollTrigger: {
          trigger: panel,
          start:
            window.innerWidth > 969 ? "center center" : "top 18%",
          end: window.innerWidth > 969 ? "150% center" : "bottom 20%",
          pin: true,
          pinSpacing,
          markers: false,
          scrub: 1,
          onEnter: () => {
            panel.classList.add("active");
            panel.classList.remove("opacity-0");
            updateCounter(index, "add");
            setFrame(index);
          },
          onLeave: () => {
            if (index !== panels.length - 1) {
              panel.classList.remove("active");
              panel.classList.add("opacity-0");
              updateCounter(index, "remove");
            }
          },
          onEnterBack: () => {
            panel.classList.add("active");
            panel.classList.remove("opacity-0");
            updateCounter(index, "add");
            setFrame(index);
          },
          onLeaveBack: () => {
            if (index !== 0) {
              panel.classList.remove("active");
              panel.classList.add("opacity-0");
              updateCounter(index, "remove");
            }
          },
        },
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <section
      id="cdp-intro"
      className="relative"
      style={{ background }}
      ref={containerRef}
    >
      <div className="flex px-4 md:px-8 flex-col relative items-center justify-center">
        <div className="panel-holder w-full z-10">
          {data.steps?.map((step, i) => (
            <div
              key={`panel-${i}`}
              className={`step-panel ${i !== 0 ? "opacity-0" : "active"
                } h-[65lvh] md:h-[75lvh] pt-6`}
            >
              <div className="bg-white/60 rounded-3xl md:rounded-[2.5rem] shadow-sm w-full  ">
                <div className="px-8 md:px-10 lg:px-16 pt-8 md:pt-12 pb-4">
                  {data.heading && (
                    <h2 className="text-purple font-bold text-md sm:text-xl md:text-2xl lg:text-3xl uppercase tracking-tight">
                      {data.heading}
                    </h2>
                  )}
                </div>

                <div className="px-8 md:px-10 lg:px-16 pb-8 md:pb-12">
                  <div className="flex lg:flex-row flex-col gap-5 md:gap-8 items-center">
                    <div className="lg:w-[60%] w-full">
                      {data.steps.map((s, idx) => (
                        <Image
                          key={s.id}
                          src={getStrapiMedia(
                            s.image?.url || ""
                          )}
                          width={800}
                          height={600}
                          alt={
                            s.image
                              ?.alternativeText ||
                            s.title ||
                            ""
                          }
                          className={`${frame === idx
                              ? "scale-100"
                              : "scale-0 !h-0 !w-0"
                            } w-full h-auto object-contain`}
                        />
                      ))}
                    </div>

                    <div className="lg:w-[40%] w-full">
                      <div className="flex flex-row items-start gap-5 md:gap-8">
                        <div
                          className="stepCounter flex flex-col gap-y-2 gap-x-4 justify-center"
                          ref={counterRefs.current[i]}
                        >
                          {data.steps.map((_, j) => (
                            <span
                              key={`counter-${i}-${j}`}
                              className={`${i === 0 &&
                                  j === 0
                                  ? "active"
                                  : ""
                                } stepCounterBtn text-sm md:text-base flex items-center justify-center w-8 md:w-10 h-auto aspect-square border-2 border-gray-300 rounded-full leading-[1] text-gray-400 font-semibold transition-all duration-300`}
                            >
                              {j + 1}
                            </span>
                          ))}
                        </div>

                        <div className="inner flex-1">
                          <h3 className="text-purple font-bold text-4xl md:text-5xl lg:text-6xl mb-4">
                            {step.title}
                          </h3>
                          <p className="text-gray-600 text-base md:text-xl lg:text-2xl leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
